/**
 * TaskGroupBrowser — Browse hierarchical task group templates by category.
 *
 * Displays task groups loaded from system configuration (ADL, medication, clinical,
 * nutrition, social, safety) with expandable child task lists.
 */

import { useMemo, useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Card,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconClock,
  IconHeart,
  IconList,
  IconShieldCheck,
  IconUser,
} from '@tabler/icons-react';
import { useTaskGroups } from '../hooks/useTaskGroups';
import type { ChildTask, TaskGroup } from '../hooks/useTaskGroups';

// ─── Category Metadata ───────────────────────────────────────────────────────

const CATEGORY_META: Record<string, { label: string; icon: string; color: string; description: string }> = {
  ADL: {
    label: 'Activities of Daily Living',
    icon: 'heart',
    color: 'pink',
    description: 'Bathing, dressing, toileting, mobility, eating',
  },
  MEDICATION: {
    label: 'Medication Administration',
    icon: 'pill',
    color: 'violet',
    description: 'Scheduled and PRN medication passes',
  },
  CLINICAL: {
    label: 'Clinical Assessments',
    icon: 'stethoscope',
    color: 'blue',
    description: 'Vitals, skin checks, shift assessments',
  },
  NUTRITION: {
    label: 'Nutrition & Dining',
    icon: 'salad',
    color: 'green',
    description: 'Meal service, hydration, dietary monitoring',
  },
  SOCIAL: {
    label: 'Social & Activities',
    icon: 'users',
    color: 'orange',
    description: 'Recreation, engagement, therapy',
  },
  SAFETY: {
    label: 'Safety & Environment',
    icon: 'shield',
    color: 'red',
    description: 'Rounds, drills, infection control',
  },
};

const PRIORITY_COLORS: Record<string, string> = {
  CRITICAL: 'red',
  URGENT: 'orange',
  HIGH: 'yellow',
  NORMAL: 'blue',
  LOW: 'gray',
};

const ROLE_LABELS: Record<string, string> = {
  cna: 'CNA',
  lpn: 'LPN',
  rn: 'RN',
  rdn: 'RDN',
  activities_director: 'Activities Dir.',
  dietary_aide: 'Dietary Aide',
  maintenance: 'Maintenance',
  charge_nurse: 'Charge Nurse',
  all_staff: 'All Staff',
};

// ─── Sub-Components ──────────────────────────────────────────────────────────

function LifeCriticalBadge() {
  return (
    <Tooltip label="Life-critical task — requires enhanced safety protocols">
      <Badge color="red" variant="filled" size="xs" leftSection={<IconAlertTriangle size={10} />}>
        Life-Critical
      </Badge>
    </Tooltip>
  );
}

function ChildTaskRow({ child }: { child: ChildTask }) {
  const roleLabel = child.assigneeRole ? (ROLE_LABELS[child.assigneeRole] ?? child.assigneeRole) : '—';

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="xs" c="dimmed">{child.sequenceOrder}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap={6}>
          <Text size="sm">{child.name}</Text>
          {child.isLifeCritical && <LifeCriticalBadge />}
          {child.required && (
            <Badge size="xs" variant="dot" color="blue">Required</Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Badge size="xs" color={PRIORITY_COLORS[child.priority] ?? 'gray'} variant="light">
          {child.priority}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group gap={4}>
          <IconUser size={12} />
          <Text size="xs">{roleLabel}</Text>
        </Group>
      </Table.Td>
      <Table.Td>
        {child.estimatedDurationMinutes ? (
          <Group gap={4}>
            <IconClock size={12} />
            <Text size="xs">{child.estimatedDurationMinutes}m</Text>
          </Group>
        ) : (
          <Text size="xs" c="dimmed">—</Text>
        )}
      </Table.Td>
      <Table.Td>
        {child.cardinality !== 'SINGLE' && (
          <Badge size="xs" variant="outline" color="cyan">
            {child.cardinality.replace('PER_', '× ')}
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        {child.dependsOn.length > 0 && (
          <Group gap={4}>
            <IconArrowRight size={12} />
            <Text size="xs" c="dimmed">{child.dependsOn.join(', ')}</Text>
          </Group>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

function GroupCard({ group }: { group: TaskGroup }) {
  const catMeta = CATEGORY_META[group.category];
  const childCount = group.children.length;
  const lifeCriticalCount = group.children.filter((c) => c.isLifeCritical).length;

  return (
    <Accordion.Item value={group.id}>
      <Accordion.Control>
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm">
            <Text fw={500}>{group.name}</Text>
            {group.isLifeCritical && <LifeCriticalBadge />}
          </Group>
          <Group gap="xs">
            <Badge size="sm" variant="light" color={catMeta?.color ?? 'gray'}>
              {childCount} tasks
            </Badge>
            {lifeCriticalCount > 0 && (
              <Badge size="sm" color="red" variant="light">
                {lifeCriticalCount} critical
              </Badge>
            )}
            {group.tags.length > 0 && group.tags.slice(0, 2).map((tag) => (
              <Badge key={tag} size="xs" variant="dot" color="gray">{tag}</Badge>
            ))}
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="sm">
          {group.description && (
            <Text size="sm" c="dimmed">{group.description}</Text>
          )}
          <Group gap="md">
            <Group gap={4}>
              <IconShieldCheck size={14} />
              <Text size="xs">Completion: {group.completionStrategy.replace(/_/g, ' ').toLowerCase()}</Text>
            </Group>
            {group.schedule && (
              <Group gap={4}>
                <IconClock size={14} />
                <Text size="xs">Scheduled</Text>
              </Group>
            )}
          </Group>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={40}>#</Table.Th>
                <Table.Th>Task</Table.Th>
                <Table.Th w={80}>Priority</Table.Th>
                <Table.Th w={100}>Assignee</Table.Th>
                <Table.Th w={70}>Duration</Table.Th>
                <Table.Th w={100}>Cardinality</Table.Th>
                <Table.Th>Dependencies</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {group.children
                .slice()
                .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
                .map((child) => (
                  <ChildTaskRow key={child.id} child={child} />
                ))}
            </Table.Tbody>
          </Table>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export interface TaskGroupBrowserProps {
  subgraph?: string;
  setting?: string;
}

export function TaskGroupBrowser({ subgraph = 'fhir', setting = 'assisted_living' }: TaskGroupBrowserProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [subgraphFilter, setSubgraphFilter] = useState<string | null>(subgraph);
  const [settingFilter, setSettingFilter] = useState<string | null>(setting);

  const { taskGroups, groupsByCategory, totalChildTasks, loading, error } = useTaskGroups({
    subgraph: subgraphFilter ?? undefined,
    setting: settingFilter ?? undefined,
    category: selectedCategory ?? undefined,
  });

  const categoryTabs = useMemo(() => {
    if (selectedCategory) {
      // When filtered by category, show only that category
      const groups = groupsByCategory[selectedCategory] ?? [];
      return [{ key: selectedCategory, groups }];
    }
    // Show all categories that have groups
    return Object.entries(groupsByCategory)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, groups]) => ({ key, groups }));
  }, [groupsByCategory, selectedCategory]);

  if (loading) {
    return (
      <Stack align="center" py="xl">
        <Loader size="lg" />
        <Text c="dimmed">Loading task group templates...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading task groups">
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="blue">
            <IconList size={20} />
          </ThemeIcon>
          <div>
            <Title order={3}>Task Group Templates</Title>
            <Text size="sm" c="dimmed">
              {taskGroups.length} groups, {totalChildTasks} child tasks
            </Text>
          </div>
        </Group>
        <Group gap="sm">
          <Select
            size="xs"
            placeholder="Subgraph"
            value={subgraphFilter}
            onChange={setSubgraphFilter}
            data={[
              { value: 'fhir', label: 'FHIR (Healthcare)' },
              { value: 'payroll', label: 'Payroll' },
              { value: 'erp', label: 'ERP' },
            ]}
            clearable
            w={160}
          />
          <Select
            size="xs"
            placeholder="Setting"
            value={settingFilter}
            onChange={setSettingFilter}
            data={[
              { value: 'assisted_living', label: 'Assisted Living' },
              { value: 'skilled_nursing', label: 'Skilled Nursing' },
              { value: 'general', label: 'General' },
            ]}
            clearable
            w={160}
          />
        </Group>
      </Group>

      {taskGroups.length === 0 ? (
        <Card withBorder p="xl">
          <Text ta="center" c="dimmed">
            No task group templates found for the selected filters.
          </Text>
        </Card>
      ) : (
        <Tabs defaultValue={categoryTabs[0]?.key}>
          <Tabs.List>
            {categoryTabs.map(({ key, groups }) => {
              const meta = CATEGORY_META[key];
              const hasLifeCritical = groups.some((g) => g.isLifeCritical);
              return (
                <Tabs.Tab key={key} value={key}>
                  <Group gap={6}>
                    {hasLifeCritical && (
                      <ThemeIcon size={14} color="red" variant="filled" radius="xl">
                        <IconHeart size={8} />
                      </ThemeIcon>
                    )}
                    <Text size="sm">{meta?.label ?? key}</Text>
                    <Badge size="xs" variant="light" color={meta?.color ?? 'gray'} circle>
                      {groups.length}
                    </Badge>
                  </Group>
                </Tabs.Tab>
              );
            })}
          </Tabs.List>

          {categoryTabs.map(({ key, groups }) => (
            <Tabs.Panel key={key} value={key} pt="md">
              <Accordion variant="separated">
                {groups.map((group) => (
                  <GroupCard key={group.id} group={group} />
                ))}
              </Accordion>
            </Tabs.Panel>
          ))}
        </Tabs>
      )}
    </Stack>
  );
}
